<template>
  <v-card elevation="1">
    <v-card-title>{{ computedTitle }}</v-card-title>
    <v-card-text>
      <v-row>
        <col12>
          <div v-if="query.groups.length > 1" class="filter-tree__op">
            <span class="filter-tree__op-label">{{ $t('filter_tree.combine_groups') }}</span>
            <v-btn-toggle
              v-model="opToggle"
              mandatory
              density="compact"
              color="primary"
              class="filter-tree__op-toggle"
            >
              <v-btn value="AND" size="small">{{ $t('filter_tree.op_and') }}</v-btn>
              <v-btn value="OR" size="small">{{ $t('filter_tree.op_or') }}</v-btn>
            </v-btn-toggle>
          </div>

          <filter-group
            v-for="(group, gi) in query.groups"
            :key="'group_' + gi"
            style="margin-top: 10px;"
            :filters="group"
            :group-index="gi"
            :show-header="query.groups.length > 1"
            :can-remove="query.groups.length > 1"
            :initial_filter="gi === 0 ? initial_filter : false"
            :non_deletable_filter_types="non_deletable_filter_types"
            :non_togglable_filter_types="non_togglable_filter_types"
            @set_value="(id, value) => set_value(gi, id, value)"
            @clear_filter_id="(id) => clear_filter_id(gi, id)"
            @add_filter="(f) => add_filter(gi, f)"
            @remove_group="remove_group(gi)"
          ></filter-group>

          <v-btn
            size="small"
            variant="outlined"
            class="filter-tree__add-group"
            @click="add_group"
          >
            <v-icon size="small" start>fas fa-plus</v-icon>
            {{ $t('filter_tree.add_group') }}
          </v-btn>

          <div style="max-width: 250px; margin-top: 16px;" v-if="show_limit">
            <v-text-field
              :label="$t('filter_tree.limit')"
              v-model="limit"
              rounded
              filled
              dense
              outlined
              type="text"
            ></v-text-field>
          </div>
          <v-btn
            v-if="show_button"
            color="primary"
            @click="load"
            style="float:none; margin-left:0px; margin-top: 15px"
            id="load_button"
            class="button"
          >
            {{ $t('filter_tree.btn_load') }}
          </v-btn>
          <div v-if="max_results !== false">
            {{ $t('settings_tasks.edit_filter_tree_max_results') }} {{ max_results }}
          </div>
        </col12>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import FilterGroup from "@/components/asset_filter/FilterGroup";
import Col12 from "@/components/layout/Col12";
import { normalizeFilterQuery, toApiFilters } from "@/components/asset_filter/filterQuery";
const { v4: uuidV4 } = require('uuid');

export default {
  name: "FilterTree",
  components: { Col12, FilterGroup },
  props: {
    max_results: { required: true },
    show_limit: { default: true },
    initial_filter: { default: false },
    show_button: { default: true },
    title: { default: null },
    loadedFilters: { default: null },
    loadedLimit: { default: null },
    non_deletable_filter_types: { default: () => [] },
    non_togglable_filter_types: { default: () => [] },
  },
  computed: {
    translatedTitle() {
      return this.$t('settings_tasks.edit_title_filter_tree');
    },
    computedTitle() {
      return this.title !== null ? this.title : this.translatedTitle;
    },
    /** Flattened rows for parents that still pass filters into FilterSaveLoad. */
    filters() {
      const out = [];
      for (const g of this.query.groups) {
        out.push(...g);
      }
      return out;
    },
    opToggle: {
      get() {
        return this.query.op === 'OR' ? 'OR' : 'AND';
      },
      set(v) {
        this.query = {
          ...this.query,
          op: v === 'OR' ? 'OR' : 'AND',
          groups: this.query.groups.map((g) => g.slice()),
        };
      },
    },
  },
  data() {
    return {
      query: normalizeFilterQuery(null),
      limit: 12,
    };
  },
  watch: {
    query: {
      handler() {
        this.$emit('update', toApiFilters(this.query), this.limit);
      },
      deep: true,
    },
    limit() {
      this.$emit('update', toApiFilters(this.query), this.limit);
    },
    loadedFilters: {
      handler(newFilters) {
        if (newFilters == null) {
          return;
        }
        const incoming = normalizeFilterQuery(newFilters);
        if (JSON.stringify(incoming) === JSON.stringify(this.query)) {
          return;
        }
        this.query = incoming;
      },
      deep: true,
      immediate: true,
    },
    loadedLimit: {
      handler(newLimit) {
        if (newLimit !== null && newLimit !== undefined) {
          this.limit = newLimit;
        }
      },
      immediate: false,
    },
  },
  methods: {
    apiFilters() {
      return toApiFilters(this.query);
    },
    prepareFilter(filter) {
      const f = { ...filter };
      f.id = f.id || uuidV4();
      if (f.options && f.options.length > 0 && !f.value && f.options[0] === 'Yes') {
        f.value = f.options[0];
      }
      if (Number(f.type) === 60 && !f.value) {
        f.value = 'random';
      }
      return f;
    },
    add_filter(groupIndex, filter) {
      const f = this.prepareFilter(filter);
      const groups = this.query.groups.map((g, i) =>
        i === groupIndex ? [...g, f] : g.slice()
      );
      this.query = { ...this.query, groups };
    },
    add_group() {
      const groups = [...this.query.groups.map((g) => g.slice()), []];
      // First time adding a second group defaults to OR (the new capability).
      const op = this.query.groups.length === 1
        ? 'OR'
        : (this.query.op === 'OR' ? 'OR' : 'AND');
      this.query = { op, groups };
    },
    remove_group(groupIndex) {
      if (this.query.groups.length <= 1) {
        return;
      }
      const groups = this.query.groups
        .filter((_, i) => i !== groupIndex)
        .map((g) => g.slice());
      this.query = {
        op: groups.length > 1 ? this.query.op : 'AND',
        groups,
      };
    },
    load() {
      this.$emit('load', toApiFilters(this.query), this.limit);
    },
    clear_filter_id(groupIndex, filter_id) {
      const group = this.query.groups[groupIndex] || [];
      const filterToRemove = group.find((f) => f.id === filter_id);
      if (filterToRemove && this.non_deletable_filter_types.includes(filterToRemove.type)) {
        return;
      }
      const groups = this.query.groups.map((g, i) =>
        i === groupIndex ? g.filter((f) => f.id !== filter_id) : g.slice()
      );
      this.query = { ...this.query, groups };
    },
    set_value(groupIndex, filter_id, value) {
      const groups = this.query.groups.map((g, i) => {
        if (i !== groupIndex) {
          return g.slice();
        }
        return g.map((f) => {
          if (f.id !== filter_id) {
            return f;
          }
          if (this.non_togglable_filter_types.includes(f.type) && Number(f.type) === 41) {
            return { ...f, value: 'Yes' };
          }
          return { ...f, value };
        });
      });
      this.query = { ...this.query, groups };
    },
    async loadFilters(filters, limit) {
      const incoming = normalizeFilterQuery(filters);
      try {
        const filterOptionsResponse = await this.$api.asset.filter_options();
        const filterOptions = Array.isArray(filterOptionsResponse) ? filterOptionsResponse : [];
        const enrich = (savedFilter) => {
          const matchingOption = filterOptions.find((opt) => opt.type === savedFilter.type);
          if (matchingOption) {
            const enriched = JSON.parse(JSON.stringify(matchingOption));
            enriched.id = savedFilter.id || uuidV4();
            enriched.value = savedFilter.value !== undefined ? savedFilter.value : enriched.value;
            return enriched;
          }
          return { ...savedFilter, id: savedFilter.id || uuidV4() };
        };
        this.query = {
          op: incoming.op,
          groups: incoming.groups.map((g) => g.map(enrich)),
        };
      } catch (error) {
        console.error('Error enriching filters:', error);
        this.query = {
          op: incoming.op,
          groups: incoming.groups.map((g) =>
            g.map((f) => ({ ...f, id: f.id || uuidV4() }))
          ),
        };
      }
      if (limit !== null && limit !== undefined) {
        this.limit = limit;
      }
    },
  },
};
</script>

<style scoped>
.filter-tree__op {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  margin-bottom: 4px;
}
.filter-tree__op-label {
  font-size: 0.9rem;
  opacity: 0.8;
}
.filter-tree__add-group {
  margin-top: 4px;
}
</style>
